/**
 * PHI redaction helpers. Default-on; the parent component decides when to
 * reveal raw values via a session-scoped "Reveal PHI" toggle (audit-logged).
 *
 * The redaction patterns target the kinds of data EHRs leak in URLs, bodies,
 * and headers: patient IDs, names, DOB, phone, email, addresses, SSN-shaped
 * numbers. We replace with `[REDACTED:type]` tokens so the structure stays
 * readable for debugging.
 *
 * Phase E will expand the rules + tighten the JSON walker. This is the
 * first cut so Phase D can build against it.
 */

const RX_EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// (?<!\w) not \b at the start, so a leading "(" in "(303) 555-1234" is included in
// the match rather than left dangling (\b can't anchor between a space and a paren).
const RX_PHONE_US = /(?<!\w)(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g;
const RX_SSN = /\b\d{3}-\d{2}-\d{4}\b/g;
const RX_DOB = /\b(?:19|20)\d{2}-\d{2}-\d{2}\b/g; // ISO date — coarse but practical for EHRs
const RX_LONG_DIGITS = /\b\d{6,}\b/g; // patient/MRN-shaped numbers

export type RedactKind = 'url' | 'body' | 'id' | 'string';

/**
 * Redact a freeform string. URLs get param-by-param treatment; everything
 * else gets the regex pass.
 */
export function redactString(input: string, kind: RedactKind = 'string'): string {
  if (!input) return input;
  if (kind === 'url') return redactUrl(input);
  return input
    .replace(RX_EMAIL, '[REDACTED:email]')
    .replace(RX_PHONE_US, '[REDACTED:phone]')
    .replace(RX_SSN, '[REDACTED:ssn]')
    .replace(RX_DOB, '[REDACTED:dob]')
    .replace(RX_LONG_DIGITS, '[REDACTED:id]');
}

/** Redact obvious patient-identifying query params; leave the path. */
function redactUrl(url: string): string {
  try {
    const u = new URL(url);
    const sensitiveKeys = new Set(['patientid', 'pid', 'patient_id', 'mrn', 'ssn', 'dob', 'birthdate', 'email', 'phone']);
    const params = new URLSearchParams(u.search);
    for (const [k, v] of Array.from(params.entries())) {
      if (sensitiveKeys.has(k.toLowerCase())) {
        params.set(k, '[REDACTED]');
      } else if (RX_LONG_DIGITS.test(v)) {
        params.set(k, v.replace(RX_LONG_DIGITS, '[REDACTED:id]'));
      }
    }
    u.search = params.toString();
    return u.toString();
  } catch {
    return redactString(url, 'string');
  }
}

/**
 * Recursively redact a JSON value. Replaces leaf strings using `redactString`.
 * Numbers and booleans pass through. Patient-name keys get a more aggressive
 * mask regardless of the value content.
 */
export function redactJson(value: unknown, keyPath: string[] = []): unknown {
  const lastKey = keyPath[keyPath.length - 1]?.toLowerCase() ?? '';
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    // Whole-value masks for keys that are guaranteed PHI. FHIR nests these in
    // arrays (name[0].given[0], address[0].line[0]) so the leaf's own key is a
    // numeric index — key off the parent in that case. Covers both flat CSV-ish
    // keys (first/last/dob) and FHIR field names (given/family/prefix/line/…).
    const idxParent = /^\d+$/.test(lastKey) ? (keyPath[keyPath.length - 2]?.toLowerCase() ?? '') : '';
    const key = idxParent || lastKey;
    if (
      /^(first|last|middle|firstname|lastname|fullname|name|given|family|prefix|suffix|email|phone|dob|birthdate|ssn|mrn|patientid|line|city|state|postalcode|zip)$/.test(
        key,
      )
    ) {
      return '[REDACTED]';
    }
    return redactString(value, 'string');
  }
  if (typeof value === 'number') {
    // Big numbers in patient-id-shaped fields get masked
    if (/(id$|patientid|pid|mrn)/.test(lastKey) && value > 999) return '[REDACTED:id]';
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((v, i) => redactJson(v, [...keyPath, String(i)]));
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = redactJson(v, [...keyPath, k]);
    }
    return out;
  }
  return value;
}
